import { createSelector } from 'reselect';
import { StoreState } from 'store/types';
import {
  findReceiptEntityStats,
  receiptStatisticsDateRangeKey,
} from 'utils/receipt-statistics';
import { OrganisationState } from './types';
import {
  collectAssignedContactIdentifiersFromTransactions,
  isContactAssignedToTransactions,
} from 'utils/contact-transaction-usage';
import { Contact } from 'store/vat-checker/models';

const selectSignUp = (app: StoreState): OrganisationState => app.organisation;

const mergeAssignedContactIdentifiers = (
  organisation: OrganisationState,
  organisationId: string
): Set<string> => {
  const identifiers = new Set<string>();

  const addFromTransactions = (
    transactions: Parameters<
      typeof collectAssignedContactIdentifiersFromTransactions
    >[0]
  ) => {
    collectAssignedContactIdentifiersFromTransactions(transactions).forEach(
      (key) => identifiers.add(key)
    );
  };

  addFromTransactions(organisation.uploadedFileTransactionsPage?.transactions);

  if (
    organisation.allExcelTransactionsPage?.organisationId === organisationId
  ) {
    addFromTransactions(organisation.allExcelTransactionsPage.transactions);
  }

  const validation = organisation.uploadExcelTransactionsValidationData as {
    validationErrors?: Parameters<
      typeof collectAssignedContactIdentifiersFromTransactions
    >[0];
    successfulTransactions?: Parameters<
      typeof collectAssignedContactIdentifiersFromTransactions
    >[0];
  } | null;

  addFromTransactions(validation?.validationErrors);
  addFromTransactions(validation?.successfulTransactions);

  return identifiers;
};

/** Contact identifiers (uuid, provider id, name) referenced by loaded transactions. */
export const getAssignedContactIdentifiersForOrganisation = (
  organisationId?: string | null
) =>
  createSelector(
    selectSignUp,
    (organisation: OrganisationState): Set<string> => {
      if (!organisationId) return new Set<string>();
      return mergeAssignedContactIdentifiers(organisation, organisationId);
    }
  );

/** True when contact is used by any transaction currently present in store. */
export const getIsContactUsedInTransactions = (
  organisationId?: string | null,
  contact?: Contact | null
) =>
  createSelector(selectSignUp, (organisation: OrganisationState): boolean => {
    if (!organisationId || !contact) return false;
    const assignedIdentifiers = mergeAssignedContactIdentifiers(
      organisation,
      organisationId
    );
    return isContactAssignedToTransactions(contact, assignedIdentifiers);
  });

export const getOrgLoading = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['loading'] =>
    organisation.loading
);

export const getOrgError = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['error'] =>
    organisation.error
);

export const getOrgOrganisations = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['organisations'] =>
    organisation.organisations
);

/** Chart of accounts for a specific organisation (same pattern as getTaxRatesForConnection). */
export const getChartOfAccountsForOrganisation = (
  organisationId?: string | null
) =>
  createSelector(selectSignUp, (organisation: OrganisationState) => {
    if (!organisationId) return [];
    const org = organisation.organisations?.find(
      (o) => o?.uuid === organisationId
    );
    return (org?.chartOfAccounts ??
      []) as import('store/chart-of-accounts/models').CoaAccountRow[];
  });

export const getOrgMappings = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['mappings'] =>
    organisation.mappings
);

export const getOrgMappedOrganisations = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['mappedOrganisations'] =>
    organisation.mappedOrganisations
);

export const getOrgMappedReturns = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['mappedReturns'] =>
    organisation.mappedReturns
);

export const getOrgShowActions = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['showActions'] =>
    organisation.showActions
);

export const getOrgShowConnections = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['showConnections'] =>
    organisation.showConnections
);

export const getSelectedEntityAccounts = createSelector(
  selectSignUp,
  (
    organisation: OrganisationState
  ): OrganisationState['selectedEntityAccounts'] =>
    organisation.selectedEntityAccounts
);

export const getOrganisationViesContacts = createSelector(
  selectSignUp,
  (
    organisation: OrganisationState
  ): OrganisationState['organisationViesContacts'] =>
    organisation.organisationViesContacts
);

export const getUploadTransactionsTemp = createSelector(
  selectSignUp,
  (
    organisation: OrganisationState
  ): OrganisationState['uploadTransactionsTemp'] =>
    organisation.uploadTransactionsTemp
);

export const getUploadExcelTransactionsStatus = createSelector(
  selectSignUp,
  (
    organisation: OrganisationState
  ): OrganisationState['uploadExcelTransactionsStatus'] =>
    organisation.uploadExcelTransactionsStatus
);

export const getUploadExcelTransactionsValidationData = createSelector(
  selectSignUp,
  (
    organisation: OrganisationState
  ): OrganisationState['uploadExcelTransactionsValidationData'] =>
    organisation.uploadExcelTransactionsValidationData
);

export const getUploadedFileTransactionsPage = createSelector(
  selectSignUp,
  (
    organisation: OrganisationState
  ): OrganisationState['uploadedFileTransactionsPage'] =>
    organisation.uploadedFileTransactionsPage
);

export const getAllExcelTransactionsPage = createSelector(
  selectSignUp,
  (
    organisation: OrganisationState
  ): OrganisationState['allExcelTransactionsPage'] =>
    organisation.allExcelTransactionsPage
);

export const getAllExcelTransactionsLoading = createSelector(
  selectSignUp,
  (
    organisation: OrganisationState
  ): OrganisationState['allExcelTransactionsLoading'] =>
    organisation.allExcelTransactionsLoading
);

/** Drawer batch save — optional root state guard (e.g. HMR / partial store). */
export const getExcelDrawerTransactionsBatchSaving = (
  state: StoreState
): boolean => state?.organisation?.excelDrawerTransactionsBatchSaving === true;

/** Open-drawer saved-id hops (`old → newTransactionId`). */
export const getDrawerTransactionIdMap = (
  state: StoreState
): Record<string, string> =>
  state?.organisation?.drawerTransactionIdMap ?? {};

export const getIsPostingToProvider = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['isPostingToProvider'] =>
    organisation.isPostingToProvider ?? false
);

export const getLastUploadedReceiptsBatchId = createSelector(
  selectSignUp,
  (
    organisation: OrganisationState
  ): OrganisationState['lastUploadedReceiptsBatchId'] =>
    organisation.lastUploadedReceiptsBatchId
);

export const getLastReceiptUploadStatus = createSelector(
  selectSignUp,
  (
    organisation: OrganisationState
  ): OrganisationState['lastReceiptUploadStatus'] =>
    organisation.lastReceiptUploadStatus
);

/** Used by transactions tab to turn off mass-update mode after a successful bulk update. */
export const getBulkUpdateExcelTransactionsSuccessCount = createSelector(
  selectSignUp,
  (organisation: OrganisationState): number =>
    organisation.bulkUpdateExcelTransactionsSuccessCount ?? 0
);

export const getGoogleAccessToken = createSelector(
  selectSignUp,
  (user: OrganisationState): OrganisationState['googleAccessToken'] =>
    user.googleAccessToken
);

export const getGoogleDriveStatus = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['googleDriveStatus'] =>
    organisation.googleDriveStatus
);

export const getOneDriveStatus = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['oneDriveStatus'] =>
    organisation.oneDriveStatus
);

export const getEmailCaptureStatus = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['emailCaptureStatus'] =>
    organisation.emailCaptureStatus
);

export const getFilesUploadHistory = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['filesUploadHistory'] =>
    organisation.filesUploadHistory
);

export const getScanEmailDetails = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['scanEmailDetails'] =>
    organisation.scanEmailDetails
);

export const getUploadInitFailed = createSelector(
  selectSignUp,
  (organisation: OrganisationState): OrganisationState['uploadInitFailed'] =>
    organisation.uploadInitFailed
);

export const getReceiptStatisticsAll = createSelector(
  selectSignUp,
  (organisation: OrganisationState) => organisation.receiptStatisticsAll
);

export const getReceiptStatisticsDateRange = createSelector(
  selectSignUp,
  (organisation: OrganisationState) => organisation.receiptStatisticsDateRange
);

export const getReceiptStatisticsDateRangeKey = createSelector(
  getReceiptStatisticsDateRange,
  (dateRange) => receiptStatisticsDateRangeKey(dateRange)
);

export const getReceiptStatisticsLoading = createSelector(
  selectSignUp,
  (organisation: OrganisationState) => organisation.receiptStatisticsLoading
);

export const getReceiptStatisticsEntityLoading = createSelector(
  selectSignUp,
  (organisation: OrganisationState) =>
    organisation.receiptStatisticsEntityLoading
);

export const getReceiptEntityStats = (
  connectionId: string,
  organisationId?: string
) =>
  createSelector(getReceiptStatisticsAll, (statistics) =>
    findReceiptEntityStats(statistics?.entitiesStats, {
      connectionId,
      organisationId,
    })
  );

export const getReceiptStatisticsEntityByKey = (cacheKey: string) =>
  createSelector(
    selectSignUp,
    (organisation: OrganisationState) =>
      organisation.receiptStatisticsEntityByKey?.[cacheKey]
  );

export const getHmrcFraudHeaderTestResult = createSelector(
  selectSignUp,
  (organisation: OrganisationState) => organisation.hmrcFraudHeaderTestResult
);
